import React, { useState, useEffect } from 'react';
import './SlidingDrawer.css'; // Make sure this file includes the necessary styles for the labels
import addUserIcon from './assets/add_user.png';
import { fetchWithAuth } from './utils';

interface Opportunity {
  project_name: string;
  status: string;
  start_date: string;
  due_date: string;
  priority: string;
  created_by: string;
  created_date: string;
  opportunity_id: string;
  region_code: string;
}

interface Manning {
  name: string;
  title: string;
  manning_info_id: string;
  opportunity_manning_id: number;
}

interface Label {
  label_id: string;
  label_name: string;
}

interface OpportunitySlidingDrawerProps {
  isOpen: boolean;
  selectedRowData: Opportunity | null;
  onClose: () => void;
  onUpdateSuccess: () => void;
}

const OpportunitySlidingDrawer: React.FC<OpportunitySlidingDrawerProps> = ({
  isOpen,
  selectedRowData,
  onClose,
  onUpdateSuccess,
}) => {
  const [labels, setLabels] = useState<any[]>([]);
  const [mannings, setMannings] = useState<Manning[]>([]);
  const [notes, setNotes] = useState<any[]>([]);
  const [allMannings, setAllMannings] = useState<Manning[]>([]); // Dropdown mannings
  const [allLabels, setAllLabels] = useState<Label[]>([]); // Dropdown labels
  const [selectedLabel, setSelectedLabel] = useState<string>('');
  const [selectedManning, setSelectedManning] = useState<{ [key: string]: string }>({});
  const [openAccordion, setOpenAccordion] = useState<'details' | 'notes' | 'attachments' | null>(null);
  const statusOptions = [
  'Qualification',
  'Development',
  'Submitted',
  'Hold',
  'Engaged',
  'Dead',
  'Forecasted'
  ];

  const priorityOptions = [
    'Urgent',
    'Critical',
    'High',
    'Medium',
    'Low'
  ];

  const [editedStatus, setEditedStatus] = useState<string>('');
  const [editedPriority, setEditedPriority] = useState<string>('');
  const [showUpdate, setShowUpdate] = useState<boolean>(false);
  const [editedStartDate, setEditedStartDate] = useState<string>('');
  const [editedDueDate, setEditedDueDate] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      const cachedMannings = localStorage.getItem('manning_SWR_SAC|MOD|FRE|SFO|FLD|DEN-LL|SFV|DEN|IRV|PHX|PAS');
      if (cachedMannings) {
        const parsedMannings = JSON.parse(cachedMannings);
        setAllMannings(parsedMannings);
      } else {
        fetchAllMannings();
      }
      fetchAllLabels();
      setOpenAccordion('details');
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedRowData) {
      fetchLabels();
      fetchMannings();
    }
  }, [isOpen, selectedRowData]);

  const fetchLabels = async () => {
    if (selectedRowData) {
      const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_opportunity_labels&opportunity_id=${selectedRowData.opportunity_id}`
      const data = await fetchWithAuth(url);
      setLabels(data);
    }
  };

  const fetchMannings = async () => {
    if (selectedRowData) {
      const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_opportunity_mannings&opportunity_id=${selectedRowData.opportunity_id}`
      const data = await fetchWithAuth(url);
      console.log(data)
      setMannings(data);
    }
  };

  const fetchNotes = async () => {
    if (selectedRowData) {
      const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_opportunity_notes&opportunity_id=${selectedRowData.opportunity_id}`
      const data = await fetchWithAuth(url);
      setNotes(data);
    }
  };

  const fetchAllMannings = async () => {
    const url = 'https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_all_manning&company_code=DG&region_code=SWR&office_codes=SAC|MOD|FRE|SFO|FLD|DEN-LL|SFV|DEN|IRV|PHX|PAS'
    const data = await fetchWithAuth(url);
    setAllMannings(data);
    localStorage.setItem('manning_SWR_SAC|MOD|FRE|SFO|FLD|DEN-LL|SFV|DEN|IRV|PHX|PAS', JSON.stringify(data));
  };

  const fetchAllLabels = async () => {
    const url = 'https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_labels'
    const data = await fetchWithAuth(url);
    setAllLabels(data);
  };

  const handleAddLabel = async (labelName: string) => {
    if (!selectedRowData) return;
  
    try {
      const url = 'https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=add_opportunity_label';
  
      const response = await fetchWithAuth(url, {
        method: 'POST',
        body: JSON.stringify({
          opportunity_id: selectedRowData.opportunity_id,
          label_name: labelName,
        }),
      });
  
      if (response) {
        console.log('Label added successfully!');
        fetchLabels(); // Refresh the label list
      } else {
        console.error('Failed to add label');
      }
    } catch (error) {
      console.error('Error adding label:', error);
    }
  };
  
  const toggleAccordion = (accordion: 'details' | 'notes' | 'attachments') => {
    setOpenAccordion(openAccordion === accordion ? null : accordion);
    if (accordion === 'notes' && openAccordion !== 'notes') {
      fetchNotes();
    }
  };

  const managers = mannings.filter(
    (manning) => manning.title.includes('Manager') || manning.title.includes('Director') || manning.title.includes('Partner') || manning.title.includes('Principal')
  );

  const others = mannings.filter(
    (manning) => !manning.title.includes('Manager') && !manning.title.includes('Director') && !manning.title.includes('Partner')
  );

  const handleRemoveManning = async (type: 'manager' | 'other', index: number) => {
    const list = type === 'manager' ? managers : others;
    const manningToRemove = list[index];
    console.log("manningToRemove", manningToRemove)
    if (!manningToRemove || !selectedRowData) return;
    console.log("1manningToRemove", manningToRemove)
    try {
      const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=remove_opportunity_manning&opportunity_manning_id=${manningToRemove.opportunity_manning_id}`
      const data = await fetchWithAuth(url);
      fetchMannings(); // Refresh list
    } catch (error) {
      console.error('Error removing manning:', error);
    }
  };
  

  const handleManningChange = async (group: string, manningInfoId: string) => {
    if (!selectedRowData) return;
  
    try {
      const url = 'https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=add_opportunity_manning';
  
      const response = await fetchWithAuth(url, {
        method: 'POST',
        body: JSON.stringify({
          manning_info_id: manningInfoId,
          opportunity_id: selectedRowData.opportunity_id,
        }),
      });
  
      if (response) {
        // Refresh the list of mannings
        fetchMannings();
        setSelectedManning((prev) => ({ ...prev, [group]: '' }));
        console.log('Manning added successfully!');
      } else {
        console.error('Failed to add manning');
      }
    } catch (error) {
      console.error('Error adding manning:', error);
    }
  };
  
  
    // Function to assign colors to labels based on their names
    const getColorForLabel = (label: any) => {
      const labelText = typeof label === 'string' ? label : label.label_name;
  
      const labelColors: { [key: string]: string } = {
        'PO in Hand': '#0078D7',  // Blue
        'In Progress': '#FF5733', // Orange
        'Completed': '#28A745',   // Green
        'Pending': '#FFC107',     // Yellow
        'On Hold': '#8E44AD',     // Purple
        'Cancelled': '#E74C3C',   // Red
        'Reviewed': '#17A2B8',    // Light Blue
        'Approved': '#2ECC71',    // Light Green
        // Add more label-color pairs as needed
      };
  
      // If no pre-defined color, generate a pastel color
      return labelColors[labelText] || generateRandomDarkColor();
    };
  
  // Function to generate a random dark color
  const generateRandomDarkColor = () => {
    const r = Math.floor((Math.random() * 100) + 50); // Generate a dark red value (range: 50-150)
    const g = Math.floor((Math.random() * 100) + 50); // Generate a dark green value (range: 50-150)
    const b = Math.floor((Math.random() * 100) + 50); // Generate a dark blue value (range: 50-150)
    return `rgb(${r},${g},${b})`;
  };

  const handleAddNote = async (note: string) => {
    if (!selectedRowData) return;
  
    try {
      const url = 'https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=add_opportunity_note';
  
      const response = await fetchWithAuth(url, {
        method: 'POST',
        body: JSON.stringify({
          opportunity_id: selectedRowData.opportunity_id,
          note: note,
          note_added_by: 'test user',
        }),
      });
 
      if (response) {
        console.log('Note added successfully!');
        fetchNotes(); // ✅ Make sure this is always called on success
      } else {
        console.error('Failed to add note');
      }
    } catch (error) {
      console.error('Error adding note:', error);
    }
  };
  
  const handleRemoveLabel = async (opportunityLabelId: string) => {
    if (!opportunityLabelId) return;

    try {
      const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=remove_opportunity_label&opportunity_label_id=${opportunityLabelId}`;

      const response = await fetchWithAuth(url);
      if (response) {
        console.log('Label removed successfully!');
        fetchLabels(); // Refresh label list
      } else {
        console.error('Failed to remove label');
      }
    } catch (error) {
      console.error('Error removing label:', error);
    }
  };

const groupNotesByUser = (notesArr: any[]) => {
  const grouped: { [key: string]: { name: string; date: string; notes: string[] } } = {};
  notesArr.forEach((note) => {
    const user = note.note_added_by || 'Unknown';
    // Format date in Pacific Time, keeping only date part if you want to group by date (not timestamp)
    const dateStr = note.note_date
      ? new Date(note.note_date).toLocaleDateString("en-US", { timeZone: "America/Los_Angeles" })
      : '';
    const key = `${user}__${dateStr}`;
    if (!grouped[key]) {
      grouped[key] = { name: user, date: dateStr, notes: [] };
    }
    grouped[key].notes.push(note.note);
  });
  return Object.values(grouped);
};

useEffect(() => {
  if (selectedRowData) {
    setEditedStatus(selectedRowData.status || '');
    setEditedPriority(selectedRowData.priority || '');
    setEditedStartDate(selectedRowData.start_date || '');
    setEditedDueDate(selectedRowData.due_date || '');
    setShowUpdate(false); // Hide the button until changes are made
  }
}, [selectedRowData]);



  return (
    <div className={`drawer ${isOpen ? 'open' : ''}`}>
      <div className="accordion">
        <button className="accordion-header" onClick={() => toggleAccordion('details')}>
          Opportunity Details
          <span>{openAccordion === 'details' ? '-' : '+'}</span>
        </button>
        {openAccordion === 'details' && (
          <div className="accordion-content">
            <div className="label-select-section">
              <select
                value={selectedLabel}
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  if (selectedValue) {
                    handleAddLabel(selectedValue);
                  }
                  setSelectedLabel(selectedValue);
                }}
              >
                <option value="">Add Label</option>
                {allLabels.map((label) => (
                  <option key={label.label_id} value={label.label_name}>
                    {label.label_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="details-list">
              <div className="detail-item">
                <span>Project Name:</span> {selectedRowData?.project_name}
              </div>
              <div className="detail-item">
                <span>Status:</span>
                <select
                  value={editedStatus}
                  onChange={(e) => {
                    setEditedStatus(e.target.value);
                    setShowUpdate(
                      e.target.value !== selectedRowData?.status || editedPriority !== selectedRowData?.priority
                    );
                  }}
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
              <div className="detail-item">
                <span>Start Date:</span>
                <input
                  type="date"
                  value={editedStartDate}
                  onChange={(e) => {
                    setEditedStartDate(e.target.value);
                    setShowUpdate(
                      e.target.value !== selectedRowData?.start_date ||
                      editedDueDate !== selectedRowData?.due_date ||
                      editedStatus !== selectedRowData?.status ||
                      editedPriority !== selectedRowData?.priority
                    );
                  }}
                  style={{ marginLeft: '0.5em' }}
                />
              </div>
              <div className="detail-item">
                <span>Due Date:</span>
                <input
                  type="date"
                  value={editedDueDate}
                  onChange={(e) => {
                    setEditedDueDate(e.target.value);
                    setShowUpdate(
                      editedStartDate !== selectedRowData?.start_date ||
                      e.target.value !== selectedRowData?.due_date ||
                      editedStatus !== selectedRowData?.status ||
                      editedPriority !== selectedRowData?.priority
                    );
                  }}
                  style={{ marginLeft: '0.5em' }}
                />
              </div>

              <div className="detail-item">
                <span>Priority:</span>
                <select
                  value={editedPriority}
                  onChange={(e) => {
                    setEditedPriority(e.target.value);
                    setShowUpdate(
                      editedStatus !== selectedRowData?.status || e.target.value !== selectedRowData?.priority
                    );
                  }}
                >
                  {priorityOptions.map((priority) => (
                    <option key={priority} value={priority}>
                      {priority}
                    </option>
                  ))}
                </select>
              </div>
              <div className="detail-item">
                <span>Created By:</span> {selectedRowData?.created_by}
              </div>

                {showUpdate && (
                  <button
                    className="dark-teal-updateopportunity-button"
                    onClick={async () => {
                      if (!selectedRowData) return;
                      const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=update_opportunity`;
                      await fetchWithAuth(url, {
                        method: 'POST',
                        body: JSON.stringify({
                          opportunity_id: selectedRowData.opportunity_id,
                          status: editedStatus,
                          priority: editedPriority,
                          start_date: editedStartDate,
                          due_date: editedDueDate
                        }),
                      });
                      // Remove the cached opportunities as before
                      if (selectedRowData.region_code) {
                        localStorage.removeItem(`opportunities_${selectedRowData.region_code}`);
                      }
                      setShowUpdate(false);
                      onUpdateSuccess(); // <-- This triggers the refresh in parent!
                      // Optionally, show a toast/notification for success
                    }}
                    style={{ marginLeft: '1em' }}
                  >
                    Update
                  </button>
                )}

            </div>

            <ul className="label-list">
              {labels.map((label: any, index) => (
                <li key={index} className="label-box" style={{ backgroundColor: getColorForLabel(label), position: 'relative' }}>
                  <span>{typeof label === 'string' ? label : label.label_name}</span>
                  <span
                    className="remove-label"
                    onClick={() => handleRemoveLabel(label.opportunity_label_id)}
                  >
                    ×
                  </span>

                </li>
              ))}
            </ul>


            <div className="manning-section">
              <div className="manning-column">
                <div className="label-container">
                  <img src={addUserIcon} className="manning-icon" alt="Manager Icon" />
                  <label>PM/Lead:</label>
                </div>
                <select
                  value={selectedManning['manager'] || ''}
                  onChange={(e) => handleManningChange('manager', e.target.value)}
                >
                  <option value="">Select To Add</option>
                  {allMannings
                    .filter(
                      (m) =>
                        m.title &&
                        (m.title.toLowerCase().includes('lead') || m.title.toLowerCase().includes('manager'))
                    )
                    .map((m) => (
                      <option key={m.manning_info_id} value={m.manning_info_id}>
                        {m.name} - {m.title}
                      </option>
                    ))}
                </select>

                <ul className="manning-list">
                  {managers.map((manning, index) => (
                    <li className="manning-item" key={index}>
                      <span>{manning.name}</span>
                      <span
                        className="remove-icon"
                        onClick={() => handleRemoveManning('manager', index)}
                      >
                        &times;
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="manning-column">
                <div className="label-container">
                  <img src={addUserIcon} className="manning-icon" alt="Engineer Icon" />
                  <label>Engineers:</label>
                </div>
                <select
                  value={selectedManning['other'] || ''}
                  onChange={(e) => handleManningChange('other', e.target.value)}
                >
                  <option value="">Select To Add</option>
                  {allMannings.map((m) => (
                    <option key={m.manning_info_id} value={m.manning_info_id}>
                      {m.name} - {m.title}
                    </option>
                  ))}
                </select>
                <ul className="manning-list">
                  {others.map((manning, index) => (
                    <li className="manning-item" key={index}>
                      <span>{manning.name}</span>
                      <span
                        className="remove-icon"
                        onClick={() => handleRemoveManning('other', index)}
                      >
                        &times;
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="accordion">
        <button className="accordion-header" onClick={() => toggleAccordion('notes')}>
          Opportunity Notes
          <span>{openAccordion === 'notes' ? '-' : '+'}</span>
        </button>
        {openAccordion === 'notes' && (
          <div className="accordion-content">
            <div className="grouped-notes">
              {groupNotesByUser(notes).map((group, idx) => (
                <div className="note-group" key={idx}>
                  <div className="note-header">
                    <strong>{group.name}</strong>
                    <span className="note-date">{group.date}</span>
                  </div>
                  {group.notes.map((text, noteIdx) => (
                    <p key={noteIdx} className="note-text">{text}</p>
                  ))}
                  <hr />
                </div>
              ))}
            </div>
            <textarea
              className="note-textarea"
              placeholder="Type your note and press Enter..."
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault(); // Prevents adding a new line in the textarea
                  handleAddNote(e.currentTarget.value);
                  e.currentTarget.value = ''; // Clears the textarea after submission
                }
              }}
            />
          </div>
        )}
      </div>

      <div className="accordion">
        <button className="accordion-header" onClick={() => toggleAccordion('attachments')}>
          Opportunity Attachments
          <span>{openAccordion === 'attachments' ? '-' : '+'}</span>
        </button>
        {openAccordion === 'attachments' && (
          <div className="accordion-content">
            <p>No Attachments</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OpportunitySlidingDrawer;
